import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '../ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../ui/select';
import { FinanceEntry, Apartment, INCOME_TYPES } from './types';

interface IncomeFormData {
  apartment_id: string;
  type: string;
  amount: string;
  date: string;
  description: string;
}

interface IncomeDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  selectedIncome: FinanceEntry | null;
  form: IncomeFormData;
  onFormChange: (form: IncomeFormData) => void;
  onSubmit: (e: React.FormEvent) => void;
  apartments: Apartment[];
}

export function IncomeDialog({
  open,
  onOpenChange,
  selectedIncome,
  form,
  onFormChange,
  onSubmit,
  apartments
}: IncomeDialogProps) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>
            {selectedIncome ? 'Redigera intäkt' : 'Lägg till intäkt'}
          </DialogTitle>
        </DialogHeader>
        
        <form onSubmit={onSubmit} className="space-y-4">
          <div>
            <Label>Lägenhet / Lokal</Label>
            <Select 
              value={form.apartment_id} 
              onValueChange={(v) => onFormChange({ ...form, apartment_id: v })}
            >
              <SelectTrigger className="mt-1" data-testid="income-apartment-select">
                <SelectValue placeholder="Välj lägenhet..." />
              </SelectTrigger>
              <SelectContent>
                {apartments.map(apt => (
                  <SelectItem key={apt.id} value={apt.id}>
                    Lgh {apt.apartment_number}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          
          <div>
            <Label>Typ</Label>
            <Select 
              value={form.type} 
              onValueChange={(v) => onFormChange({ ...form, type: v })}
            >
              <SelectTrigger className="mt-1" data-testid="income-type-select">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {INCOME_TYPES.map(type => (
                  <SelectItem key={type.id} value={type.id}>{type.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label>Belopp (kr)</Label>
              <Input
                type="number"
                required
                min="0"
                step="0.01"
                value={form.amount}
                onChange={(e) => onFormChange({ ...form, amount: e.target.value })}
                className="mt-1"
                placeholder="0"
                data-testid="income-amount-input"
              />
            </div>
            <div>
              <Label>Datum</Label>
              <Input
                type="date"
                required
                value={form.date}
                onChange={(e) => onFormChange({ ...form, date: e.target.value })}
                className="mt-1"
                data-testid="income-date-input"
              />
            </div>
          </div>
          
          <div>
            <Label>Beskrivning (valfritt)</Label>
            <Textarea
              value={form.description}
              onChange={(e) => onFormChange({ ...form, description: e.target.value })}
              className="mt-1"
              rows={2}
              placeholder="Extra information..."
              data-testid="income-description-input"
            />
          </div>
          
          <div className="flex justify-end gap-2 pt-2">
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
              Avbryt
            </Button>
            <Button type="submit" data-testid="income-save-button">
              {selectedIncome ? 'Uppdatera' : 'Lägg till'}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}
